/**
 * Shopee — a comissão de afiliado chega sozinha (F1) e vira lucro por lead por bloco (F2).
 *
 * A tela responde, nessa ordem: **a Shopee está conectada e importando?**, **quanto cada bloco
 * rende por pessoa?** e **qual Sub ID é de qual bloco?**. Regras que explicam o que está aqui:
 *
 *  - **Nenhum número é calculado no front** (invariante 3). Comissão, lucro por lead, faixa e
 *    lacuna chegam prontos; a única conta é trocar unidade (centavos → reais).
 *  - **`—` não é `0`** (invariante 8). Bloco sem medição mostra o travessão com a frase do
 *    que falta; dia medido sem venda mostra zero.
 *  - **O período sai da janela que o servidor mediu**, nunca do relógio do navegador.
 *  - **A senha nunca volta**: o cartão mostra só o fim dela (`…a1b2`).
 *  - **Uma cor de ação por tela**: o violeta é "Conectar"; o resto é secundário/fantasma.
 */
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  FormAlert,
  formatNumber,
  Input,
  NativeSelect,
  PageHeader,
  SkeletonRegion,
  SkeletonText,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from '@mgdlead/ui';
import { ShoppingBag } from 'lucide-react';
import { can } from '@mgdlead/shared';
import { z } from 'zod';

import {
  EARNINGS_WRITE_PERMISSION,
  SHOPEE_CONNECT_PERMISSION,
  useCampaigns,
  useLinkShopeeSubId,
  useShopeeConnect,
  useShopeeDisconnect,
  useShopeeProfitByBlock,
  useShopeeStatus,
  useShopeeSubIds,
  useShopeeSyncNow,
  useUnlinkShopeeSubId,
  type ShopeeBlockProfit,
  type ShopeeConnectionView,
  type ShopeeSubIdRow,
} from '../../../lib/api';
import { domainMessage } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import { useZodForm } from '../../../lib/useZodForm';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import {
  BANDA,
  diaCurto,
  formatarLucroPorLead,
  reaisDeCentavos,
} from '../../../features/earnings/earnings-ui';
import {
  APP_ID_REGEX,
  APP_SECRET_REGEX,
  LACUNA_DO_BLOCO,
  STATUS_DA_CONEXAO,
  periodoDaJanela,
  quandoNoFuso,
} from '../../../features/shopee/shopee-ui';

const TRAVESSAO = '—';

export function ShopeePage() {
  const { workspace, role } = useWorkspace();
  useDocumentTitle('Shopee');
  const podeConectar = can(role, SHOPEE_CONNECT_PERMISSION);
  const [conectando, setConectando] = useState(false);

  const statusQuery = useShopeeStatus(workspace.id);
  const conexao = statusQuery.data?.connection ?? null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Shopee"
        description="A comissão de afiliado chega sozinha e mostra quanto cada bloco rende por pessoa."
        action={
          podeConectar && statusQuery.data && conexao === null ? (
            <Button variant="primary" onClick={() => setConectando(true)}>
              Conectar a Shopee
            </Button>
          ) : null
        }
      />

      {statusQuery.isPending ? (
        <SkeletonRegion label="Carregando a conexão com a Shopee">
          <SkeletonText lines={3} />
        </SkeletonRegion>
      ) : statusQuery.isError ? (
        <ErrorState
          title="Não conseguimos ver a conexão com a Shopee"
          onRetry={() => void statusQuery.refetch()}
        />
      ) : conexao === null ? (
        <EmptyState
          kind="no-data"
          icon={ShoppingBag}
          title="A Shopee ainda não está conectada"
          description={
            podeConectar
              ? 'No portal de afiliados da Shopee, abra Open API e copie o AppID e a senha da API. Conferimos com a Shopee antes de salvar, e a comissão passa a chegar de hora em hora.'
              : 'Conectar a Shopee é coisa do dono do workspace. Enquanto isso, a comissão continua sendo lançada à mão no Dashboard.'
          }
          action={
            podeConectar ? (
              <Button variant="secondary" onClick={() => setConectando(true)}>
                Conectar a Shopee
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <CartaoDaConexao
            conexao={conexao}
            timezone={workspace.timezone}
            onTrocar={() => setConectando(true)}
          />
          <Leituras conexao={conexao} />
        </>
      )}

      <ConectarShopeeDialog
        open={conectando}
        onOpenChange={setConectando}
        trocando={conexao !== null}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  A conexão                                                                   */
/* -------------------------------------------------------------------------- */

function CartaoDaConexao({
  conexao,
  timezone,
  onTrocar,
}: {
  conexao: ShopeeConnectionView;
  timezone: string;
  onTrocar: () => void;
}) {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  const podeConectar = can(role, SHOPEE_CONNECT_PERMISSION);
  const podeImportar = can(role, EARNINGS_WRITE_PERMISSION);
  const importar = useShopeeSyncNow();
  const desconectar = useShopeeDisconnect();
  const [confirmando, setConfirmando] = useState(false);
  const status = STATUS_DA_CONEXAO[conexao.status];
  const IconeStatus = status.icon;
  const parado = conexao.status === 'AUTH_FAILED' || conexao.status === 'CREDENTIAL_MISSING';

  const aoImportar = async () => {
    try {
      const r = await importar.mutateAsync({ workspaceId: workspace.id });
      toast({
        title: r.queued ? 'Importação pedida' : 'Já tem uma importação a caminho',
        description: r.queued
          ? 'Os números aparecem aqui em alguns minutos.'
          : 'Espere alguns minutos antes de pedir de novo.',
      });
    } catch (erro) {
      toast({ title: domainMessage(erro) ?? 'Não deu para pedir a importação agora.' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conexão</CardTitle>
        <CardDescription>{conexao.statusMessage}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={status.tone}>
            <IconeStatus aria-hidden className="size-3.5" />
            {status.label}
          </Badge>
          <span className="text-sm text-muted-foreground">
            AppID <span className="nums text-foreground">{conexao.appId}</span> · senha{' '}
            <span className="nums text-foreground">{conexao.secretHint ?? TRAVESSAO}</span>
          </span>
        </div>

        <p className="text-sm text-muted-foreground">
          Última importação:{' '}
          <span className="text-foreground">
            {conexao.lastSuccessAt
              ? quandoNoFuso(conexao.lastSuccessAt, timezone)
              : 'ainda não houve'}
          </span>
          {conexao.measured ? (
            <>
              {' '}
              · dias medidos de {diaCurto(conexao.measured.from)} a{' '}
              {diaCurto(conexao.measured.through)}
            </>
          ) : null}
        </p>

        {conexao.stale && conexao.lastSuccessAt !== null && !parado ? (
          <FormAlert tone="warning">
            Faz mais de 3 horas que a comissão não chega. Os números abaixo são de{' '}
            {quandoNoFuso(conexao.lastSuccessAt, timezone)}.
          </FormAlert>
        ) : null}
        {conexao.lastError && !parado ? (
          <FormAlert tone="warning">{conexao.lastError}</FormAlert>
        ) : null}
        {conexao.manualDaysInWindow > 0 ? (
          <p className="text-sm text-muted-foreground">
            {conexao.manualDaysInWindow === 1
              ? '1 dia tem comissão lançada à mão'
              : `${conexao.manualDaysInWindow} dias têm comissão lançada à mão`}{' '}
            — vale o que foi digitado. Para a Shopee assumir esse dia, remova o lançamento no
            Dashboard.
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {podeImportar && !parado ? (
            <Button
              variant="secondary"
              loading={importar.isPending}
              loadingLabel="Pedindo"
              onClick={() => void aoImportar()}
            >
              Importar agora
            </Button>
          ) : null}
          {podeConectar ? (
            <Button variant={parado ? 'secondary' : 'ghost'} onClick={onTrocar}>
              Trocar senha da API
            </Button>
          ) : null}
          {podeConectar ? (
            <Button variant="ghost" onClick={() => setConfirmando(true)}>
              Desconectar
            </Button>
          ) : null}
        </div>
      </CardContent>

      <Dialog
        open={confirmando}
        onOpenChange={setConfirmando}
        title="Desconectar a Shopee?"
        description="A comissão para de chegar sozinha. O que já foi importado continua no painel."
        size="sm"
        dismissOnBackdrop={false}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmando(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={desconectar.isPending}
              loadingLabel="Desconectando"
              onClick={() =>
                void desconectar
                  .mutateAsync({ workspaceId: workspace.id })
                  .then(() => setConfirmando(false))
                  .catch((erro: unknown) =>
                    toast({ title: domainMessage(erro) ?? 'Não deu para desconectar agora.' }),
                  )
              }
            >
              Desconectar
            </Button>
          </>
        }
      />
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Lucro por bloco + Sub IDs                                                   */
/* -------------------------------------------------------------------------- */

function Leituras({ conexao }: { conexao: ShopeeConnectionView }) {
  const { workspace } = useWorkspace();
  const periodo = useMemo(() => periodoDaJanela(conexao.measured), [conexao.measured]);
  const lucro = useShopeeProfitByBlock(workspace.id, periodo);
  const subs = useShopeeSubIds(workspace.id, periodo);

  if (periodo === null) {
    return (
      <EmptyState
        kind="no-data"
        title="Ainda sem comissão importada"
        description="Assim que a primeira importação terminar, o lucro por lead de cada bloco aparece aqui."
      />
    );
  }

  const rotuloDoPeriodo = `${diaCurto(periodo.since)} a ${diaCurto(periodo.until)}`;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Lucro por lead por bloco</CardTitle>
          <CardDescription>
            {rotuloDoPeriodo}. Comissão dos pedidos pendentes e concluídos, dividida pela gente
            dentro dos grupos de cada bloco.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {lucro.isPending ? (
            <SkeletonRegion label="Carregando o lucro por bloco">
              <SkeletonText lines={4} />
            </SkeletonRegion>
          ) : lucro.isError ? (
            <ErrorState
              title="Não conseguimos montar o lucro por bloco"
              onRetry={() => void lucro.refetch()}
            />
          ) : (
            <TabelaDeBlocos dados={lucro.data} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sub IDs</CardTitle>
          <CardDescription>
            Diga de qual bloco é cada Sub ID do seu link de afiliado. Sub ID sem bloco fica em “Sem
            bloco” — nunca é dividido entre os blocos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subs.isPending ? (
            <SkeletonRegion label="Carregando os Sub IDs">
              <SkeletonText lines={4} />
            </SkeletonRegion>
          ) : subs.isError ? (
            <ErrorState
              title="Não conseguimos ver os Sub IDs"
              onRetry={() => void subs.refetch()}
            />
          ) : (
            <TabelaDeSubIds linhas={subs.data} />
          )}
        </CardContent>
      </Card>
    </>
  );
}

function TabelaDeBlocos({ dados }: { dados: ShopeeBlockProfit }) {
  if (dados.blocks.length === 0) {
    return (
      <EmptyState
        kind="no-data"
        compact
        title="Nenhum bloco ainda"
        description="Crie um bloco em Campanhas e ligue um Sub ID a ele."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Bloco</TableHead>
          <TableHead numeric>Comissão</TableHead>
          <TableHead numeric>Pedidos</TableHead>
          <TableHead numeric>Lucro por lead</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {dados.blocks.map((b) => {
          const banda = b.band ? BANDA[b.band] : null;
          const IconeBanda = banda?.icon;
          return (
            <TableRow key={b.campaignId}>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{b.campaignName}</span>
                  <span className="text-xs text-muted-foreground">
                    {b.subIds.length > 0 ? b.subIds.join(', ') : 'sem Sub ID'}
                  </span>
                </div>
              </TableCell>
              <TableCell numeric>
                {b.commissionCents === null ? TRAVESSAO : reaisDeCentavos(b.commissionCents)}
              </TableCell>
              <TableCell numeric>
                {b.ordersCount === null ? TRAVESSAO : formatNumber(b.ordersCount)}
              </TableCell>
              <TableCell numeric>
                {b.profitPerLeadMillicents === null ? (
                  <div className="flex flex-col items-end">
                    <span className="nums">{TRAVESSAO}</span>
                    {b.gap ? (
                      <span className="text-xs text-muted-foreground">
                        {LACUNA_DO_BLOCO[b.gap]}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <span className="nums">
                        {formatarLucroPorLead(b.profitPerLeadMillicents)}
                      </span>
                      {banda && IconeBanda ? (
                        <Badge tone={banda.tone}>
                          <IconeBanda aria-hidden className="size-3.5" />
                          {banda.label}
                        </Badge>
                      ) : null}
                    </div>
                    {b.daysOutOfAverage > 0 ? (
                      <span className="text-xs text-muted-foreground">
                        {b.daysOutOfAverage === 1
                          ? '1 dia ficou fora da conta por falta de contagem'
                          : `${b.daysOutOfAverage} dias ficaram fora da conta por falta de contagem`}
                      </span>
                    ) : null}
                  </div>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell>
            <span className="font-medium">Sem bloco</span>
          </TableCell>
          <TableCell numeric>
            {dados.unattributed.commissionCents === null
              ? TRAVESSAO
              : reaisDeCentavos(dados.unattributed.commissionCents)}
          </TableCell>
          <TableCell numeric>
            {dados.unattributed.ordersCount === null
              ? TRAVESSAO
              : formatNumber(dados.unattributed.ordersCount)}
          </TableCell>
          <TableCell className="text-right text-xs text-muted-foreground">
            Pedidos sem Sub ID ligado a um bloco.
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}

function TabelaDeSubIds({ linhas }: { linhas: ShopeeSubIdRow[] }) {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  const podeLigar = can(role, EARNINGS_WRITE_PERMISSION);
  const campanhas = useCampaigns(workspace.id);
  const ligar = useLinkShopeeSubId();
  const desligar = useUnlinkShopeeSubId();

  if (linhas.length === 0) {
    return (
      <EmptyState
        kind="zero"
        compact
        title="Nenhum pedido com Sub ID nestes dias"
        description="Use Sub IDs no link de afiliado (um por bloco) para separar a comissão de cada um."
      />
    );
  }

  const opcoes = [
    { value: '', label: 'Sem bloco' },
    ...(campanhas.data?.campaigns ?? []).map((c) => ({ value: c.id, label: c.name })),
  ];

  const aoEscolher = async (linha: ShopeeSubIdRow, campaignId: string) => {
    try {
      if (campaignId === '') {
        await desligar.mutateAsync({ workspaceId: workspace.id, subId: linha.subId });
      } else {
        await ligar.mutateAsync({ workspaceId: workspace.id, subId: linha.subId, campaignId });
      }
    } catch (erro) {
      toast({ title: domainMessage(erro) ?? 'Não deu para salvar o bloco deste Sub ID.' });
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Sub ID</TableHead>
          <TableHead numeric>Pedidos</TableHead>
          <TableHead numeric>Comissão</TableHead>
          <TableHead>Bloco</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {linhas.map((l) => (
          <TableRow key={l.subId}>
            <TableCell className="nums font-medium">{l.subId}</TableCell>
            <TableCell numeric>
              {l.ordersCount === null ? TRAVESSAO : formatNumber(l.ordersCount)}
            </TableCell>
            <TableCell numeric>
              {l.commissionCents === null ? TRAVESSAO : reaisDeCentavos(l.commissionCents)}
            </TableCell>
            <TableCell>
              {podeLigar ? (
                <NativeSelect
                  aria-label={`Bloco do Sub ID ${l.subId}`}
                  size="sm"
                  value={l.campaignId ?? ''}
                  options={opcoes}
                  disabled={ligar.isPending || desligar.isPending}
                  onChange={(e) => void aoEscolher(l, e.target.value)}
                />
              ) : (
                <span>{l.campaignName ?? 'Sem bloco'}</span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/* -------------------------------------------------------------------------- */
/*  Conectar / trocar a senha                                                   */
/* -------------------------------------------------------------------------- */

export const conectarShopeeSchema = z.object({
  appId: z
    .string()
    .trim()
    .regex(APP_ID_REGEX, 'O AppID tem só números. Copie do portal de afiliados (Open API).'),
  appSecret: z
    .string()
    .trim()
    .min(1, 'Cole a senha da API.')
    .regex(APP_SECRET_REGEX, 'A senha da API não tem espaço nem acento. Copie de novo.'),
});

const FORM_ID = 'conectar-shopee';

function ConectarShopeeDialog({
  open,
  onOpenChange,
  trocando,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trocando: boolean;
}) {
  const { workspace } = useWorkspace();
  const conectar = useShopeeConnect();
  const form = useZodForm({
    schema: conectarShopeeSchema,
    initialValues: { appId: '', appSecret: '' },
    onSubmit: async (values) => {
      try {
        await conectar.mutateAsync({ workspaceId: workspace.id, ...values });
      } catch (erro) {
        // A recusa da Shopee vem com a causa escrita (e sem o segredo).
        throw domainMessage(erro) ?? erro;
      }
      form.reset({ appId: '', appSecret: '' });
      onOpenChange(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={trocando ? 'Trocar a senha da API da Shopee' : 'Conectar a Shopee'}
      description="Conferimos com a Shopee antes de salvar. A senha fica guardada cifrada e não aparece mais na tela — só o final dela."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={form.pending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={form.pending}
            loadingLabel="Conferindo com a Shopee"
            form={FORM_ID}
            type="submit"
          >
            {trocando ? 'Salvar' : 'Conectar'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-3">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}
        <Field
          label="AppID"
          hint="No portal de afiliados da Shopee, em Open API."
          error={form.errors.appId}
          required
        >
          {(control) => (
            <Input
              {...control}
              name="appId"
              inputMode="numeric"
              autoComplete="off"
              placeholder="18123450001"
              value={form.values.appId}
              invalid={form.errors.appId !== undefined}
              onChange={(e) => form.setField('appId', e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Senha da API"
          hint="A senha (secret) gerada no mesmo lugar do AppID."
          error={form.errors.appSecret}
          required
        >
          {(control) => (
            <Input
              {...control}
              name="appSecret"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={form.values.appSecret}
              invalid={form.errors.appSecret !== undefined}
              onChange={(e) => form.setField('appSecret', e.target.value)}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
